import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';

import { api } from '@/api';
import { OrganizationTypes } from '@/common/constants';
import { API_URL, IOrgBalanceDetailResponse } from '@/dtos';

import { BankingBalanceDetailItem } from '../types';

const useOrganizationBankingBalanceDetailsQuery = (orgId?: string | number) => {
  const { data, isLoading, refetch } = useQuery(
    [API_URL.OMNI_BALANCES_ORGANIZATIONS_DETAILS_BY_ORG_ID, orgId],
    () => api.get<IOrgBalanceDetailResponse>(
      API_URL.OMNI_BALANCES_ORGANIZATIONS_DETAILS_BY_ORG_ID(orgId!)
    ),
    {
      enabled: Boolean(orgId),
    }
  );

  const formattedData = useMemo((): BankingBalanceDetailItem | undefined => {
    if (!data?.data) return undefined;

    const d = data.data;

    return {
      orgId: d?.org_id,
      orgName: d?.org_name,
      orgType: d?.org_type as unknown as OrganizationTypes,
      status: d?.status,
      isLocked: d?.is_locked,
      hsaBalance: d?.hsa_balance || 0,
      notionalBalance: d?.notional_balance || 0,
      employerCalculatedPrefund: d?.calculated_prefund,
      employerPrefundHolding: d?.prefund_holding,
      employerReplenishmentToBeInvoiced: d?.replenishment_to_be_invoiced,
      employerReplenishmentNotReceived: d?.replenishment_not_received,
      employeeContributionsTotalReceived: d?.employee_contributions_total_received,
      employeeContributionsNotReceivedYet: d?.employee_contributions_not_received_yet,
      lastUpdatedAt: d?.last_updated_at ? String(d?.last_updated_at) : undefined,
      plans: d?.plans?.map((plan) => ({
        planId: plan?.plan_id,
        balance: plan?.balance ?? 0,
        planYear: plan?.plan_year,
        planType: plan?.plan_type,
        planName: plan?.plan_name,
        accountId: plan?.plan_omnibus_account_id,
        isPlanLevelFunding: plan?.is_plan_level_funding,
        fundingMethod: plan?.funding_method,
        availableBalanceType: plan?.available_balance_type,
        balances: plan?.balances ? {
          totalSpend: plan?.balances?.total_spend,
          rolledIn: plan?.balances?.rolled_in,
          rolledOut: plan?.balances?.rolled_out,
          miscellaneousTransactions: plan?.balances?.miscellaneous_transactions,
          adjustmentsNotReceived: plan?.balances?.adjustments_not_received,
          adjustmentsTotalReceived: plan?.balances?.adjustments_total_received,
          fundingMethodSpecific: plan?.balances?.funding_method_specific ? {
            calculatedPrefund: plan?.balances?.funding_method_specific.calculated_prefund,
            prefundHolding: plan?.balances?.funding_method_specific.prefund_holding,
            replenishmentToBeInvoiced: plan?.balances?.funding_method_specific.replenishment_to_be_invoiced,
            replenishmentNotReceived: plan?.balances?.funding_method_specific.replenishment_not_received,
            contributionsTotalReceived: plan?.balances?.funding_method_specific.contributions_total_received,
            contributionsNotYetReceived: plan?.balances?.funding_method_specific.contributions_not_yet_received,
          } : undefined,
        } : undefined,
      })) || [],
    };
  }, [data]);

  return {
    formattedData,
    isLoading,
    refetch,
  };
};

export default useOrganizationBankingBalanceDetailsQuery;
